import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { getPortalGiftCards } from "../../api/clientPortalPublic";
import AuthRequiredCard from "../../components/clientPortal/AuthRequiredCard";
import { useClientPortalAuth } from "../../context/ClientPortalAuthContext";
import { formatApiError } from "../../utils/formatApiError";

const LOCK_TITLE = "Gift Cards are available after your account is activated.";
const LOCK_BODY =
  "Rewards and Gift Cards will appear once the salon activates your access. If it's not activated within 48 hours, please contact the salon or ask at your appointment.";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
};

export default function PortalGiftCards() {
  const { salonId } = useOutletContext();
  const { token, client, clientMe } = useClientPortalAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [giftCards, setGiftCards] = useState([]);

  const moneyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }),
    []
  );

  useEffect(() => {
    if (!token || client) return;
    clientMe().catch(() => {});
  }, [token, client, clientMe]);

  const isActivated = client?.portal_is_activated === true;
  const showLoginPrompt = !token;
  const showActivationLock = token && client && !isActivated;

  useEffect(() => {
    if (!token || !isActivated) return;
    let active = true;
    const loadGiftCards = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getPortalGiftCards(token);
        if (!active) return;
        setGiftCards(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        setError(formatApiError(err));
        setGiftCards([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    loadGiftCards();
    return () => {
      active = false;
    };
  }, [token, isActivated]);

  if (showLoginPrompt) {
    return (
      <AuthRequiredCard
        title="Sign in to view Gift Cards"
        subtitle="Please log in or register to access your gift cards."
        salonId={salonId}
      />
    );
  }

  if (showActivationLock) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
          {LOCK_TITLE}
        </h2>
        <p className="mt-2 text-sm md:text-base text-slate-600">{LOCK_BODY}</p>
        <div className="mt-5">
          <Link
            to={`/portal/salon/${salonId}/book`}
            className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800"
          >
            Book an appointment
          </Link>
        </div>
      </div>
    );
  }

  if (token && !client) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600">
        Loading your account...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
            Gift Cards
          </h2>
          <p className="mt-1 text-sm md:text-base text-slate-600">
            View your gift card balances and recent activity.
          </p>
        </div>

        {error && (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="mt-4 text-sm text-slate-500">Loading gift cards...</div>
        ) : giftCards.length === 0 ? (
          <div className="mt-4 text-sm text-slate-500">No gift cards found.</div>
        ) : (
          <div className="mt-4 space-y-3">
            {giftCards.map((card, index) => (
              <div
                key={card.code || card.purchased_at || index}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">
                      {card.code || "Gift Card"}
                    </div>
                    <div className="text-xs text-slate-500">
                      Purchased {formatDate(card.purchased_at)}
                    </div>
                    <div className="mt-1 text-xs text-slate-600">
                      Total: {moneyFormatter.format(Number(card.total_amount || 0))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Remaining</div>
                    <div className="text-base font-semibold text-slate-900">
                      {moneyFormatter.format(Number(card.remaining || 0))}
                    </div>
                    {card.is_expired && (
                      <div className="mt-1 text-xs text-rose-600">Expired</div>
                    )}
                  </div>
                </div>
                {(card.is_owner || card.is_purchaser) && (
                  <div className="mt-2 text-xs text-slate-500">
                    {card.is_owner ? "Owner" : ""}
                    {card.is_owner && card.is_purchaser ? " / " : ""}
                    {card.is_purchaser ? "Purchaser" : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
